/**
 * PasskeySetupModal Component
 *
 * Modal for registering a new passkey.
 * Guides the user through the WebAuthn registration flow:
 * 1. Introduction/confirmation
 * 2. Device name input
 * 3. Browser WebAuthn ceremony
 * 4. Success confirmation
 */

import { useState, type FormEvent } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import { passkeyApi } from '@/services/passkey';
import { ApiClientError } from '@/services/api';
import {
  isWebAuthnSupported,
  isPlatformAuthenticatorAvailable,
  detectDeviceName,
  WebAuthnError,
} from '@/utils/webauthn';

export interface PasskeySetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type SetupStep = 'intro' | 'registering' | 'success';

/**
 * Modal for setting up a new passkey
 */
export default function PasskeySetupModal({
  isOpen,
  onClose,
  onSuccess,
}: PasskeySetupModalProps) {
  const [step, setStep] = useState<SetupStep>('intro');
  const [deviceName, setDeviceName] = useState(detectDeviceName());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasPlatformAuth, setHasPlatformAuth] = useState<boolean | null>(null);

  // Check for platform authenticator on mount
  const checkPlatformAuth = async () => {
    const available = await isPlatformAuthenticatorAvailable();
    setHasPlatformAuth(available);
  };

  // Initialize when modal opens
  if (isOpen && hasPlatformAuth === null) {
    checkPlatformAuth();
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    setStep('registering');

    try {
      await passkeyApi.registerPasskey(deviceName.trim() || undefined);
      setStep('success');
    } catch (err) {
      setStep('intro');
      if (err instanceof WebAuthnError) {
        if (err.type === 'cancelled') {
          // User cancelled, don't show error
          setError(null);
        } else {
          setError(err.message);
        }
      } else if (err instanceof ApiClientError) {
        setError(err.detail);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to register passkey. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setStep('intro');
    setDeviceName(detectDeviceName());
    setError(null);
    setIsLoading(false);
    onClose();
  };

  const handleDone = () => {
    onSuccess();
    handleClose();
  };

  if (!isOpen) return null;

  // Check if WebAuthn is supported
  if (!isWebAuthnSupported()) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center"
        role="dialog"
        aria-modal="true"
        aria-labelledby="passkey-modal-title"
      >
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={handleClose}
          aria-hidden="true"
        />
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
          <h2 id="passkey-modal-title" className="text-lg font-semibold text-gray-900 mb-4">
            Passkeys Not Supported
          </h2>
          <Alert variant="warning" className="mb-4">
            Your browser does not support passkeys. Please use a modern browser like Chrome, Safari, Firefox, or Edge.
          </Alert>
          <Button onClick={handleClose} className="w-full">
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="passkey-modal-title"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={step === 'registering' ? undefined : handleClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
        {/* Step: Introduction */}
        {step === 'intro' && (
          <>
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 mb-4">
                <svg
                  className="h-6 w-6 text-primary-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4"
                  />
                </svg>
              </div>
              <h2 id="passkey-modal-title" className="text-lg font-semibold text-gray-900 mb-2">
                Add a Passkey
              </h2>
              <p className="text-sm text-gray-600">
                {hasPlatformAuth
                  ? 'Use your device\'s built-in biometric authentication (Face ID, Touch ID, Windows Hello) to sign in quickly and securely.'
                  : 'Use a security key or your device\'s built-in authentication to sign in quickly and securely.'}
              </p>
            </div>

            {error && (
              <Alert variant="error" className="mb-4" onDismiss={() => setError(null)}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Input
                label="Device name"
                type="text"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder="e.g., iPhone, MacBook, YubiKey"
                maxLength={255}
                hint="A name to help you identify this passkey"
                autoFocus
              />

              <div className="flex gap-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="flex-1"
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  isLoading={isLoading}
                >
                  Continue
                </Button>
              </div>
            </form>

            <p className="mt-4 text-xs text-gray-500 text-center">
              You may be prompted to use your device&apos;s biometric authentication or a security key.
            </p>
          </>
        )}

        {/* Step: Registering (WebAuthn ceremony in progress) */}
        {step === 'registering' && (
          <div className="text-center py-8">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-primary-100 mb-4">
              <svg
                className="h-8 w-8 text-primary-600 animate-pulse"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Complete Authentication
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Follow your browser&apos;s prompts to complete passkey registration.
            </p>
            <p className="text-xs text-gray-500">
              This may involve using Face ID, Touch ID, Windows Hello, or a security key.
            </p>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Passkey Added
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Your passkey &quot;{deviceName || 'New Passkey'}&quot; has been registered successfully. You can now use it to sign in.
            </p>
            <Button onClick={handleDone} className="w-full">
              Done
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
